import _ from 'underscore';

import {EventConstants} from 'constants/EventConstants';
import {BasicFolderItem} from 'folder-items/FileConstants';
import AppDispatcher, {FluxAction} from 'lib/Dispatcher';
import Store from 'lib/Store';
import {SchemaValidationErrorRecord} from 'schemas/SchemaValidationConstants';

export interface ValidationErrorData {
  convertedNameErrors: {errors: Array<string>; warnings: Array<string>};
  deletedNameErrors: {errors: Array<string>; warnings: Array<string>};
  existingNameErrors: {errors: Array<string>; warnings: Array<string>};
  keepNameErrors: {errors: Array<string>; warnings: Array<string>};
  setNameFromPartsErrors: {errors: Array<string>; warnings: Array<string>};
  replaceNamesFromPartsErrors: {errors: Array<string>; warnings: Array<string>};
  replaceIdAndNameFromPartsErrors: {errors: Array<string>; warnings: Array<string>};
  warningCode: string;
  duplicateItemApiIds?: Array<string>;
}

export interface RegistryValidatorStoreState {
  isLoading: boolean;
  myCollectionErrorsByFileId: {[key: string]: ValidationErrorData} | null;
  orgCollectionErrorsByFileId: {[key: string]: ValidationErrorData} | null;
  results: Array<BasicFolderItem>;
  addedFileIds: Array<string>;
}

/**
 * Store controlling the data for RegistryValidation, as used in RegisterFileModal and MetadataEditor.
 */
class RegistryValidatorStore extends Store {
  isLoading!: boolean;
  myCollectionErrorsByFileId!: {[key: string]: ValidationErrorData} | null;
  orgCollectionErrorsByFileId!: {[key: string]: ValidationErrorData} | null;
  results!: Array<BasicFolderItem>;
  addedFileIds!: Array<string>;
  tagErrorsByFileId!: {[tagSchemaId: string]: Array<SchemaValidationErrorRecord>};

  resetState(): void {
    this.myCollectionErrorsByFileId = null;
    this.orgCollectionErrorsByFileId = null;
    this.isLoading = false;
    this.results = [];
    this.addedFileIds = [];
    this.tagErrorsByFileId = {};
  }

  getState(): RegistryValidatorStoreState {
    return {
      isLoading: this.isLoading,
      myCollectionErrorsByFileId: this.myCollectionErrorsByFileId,
      orgCollectionErrorsByFileId: this.orgCollectionErrorsByFileId,
      results: this.results,
      addedFileIds: this.addedFileIds,
    };
  }

  getTagErrors(fileId: string): Array<SchemaValidationErrorRecord> | null {
    return this.tagErrorsByFileId[fileId];
  }

  resetModalState(): void {
    this.myCollectionErrorsByFileId = null;
    this.orgCollectionErrorsByFileId = null;
    this.isLoading = false;
    this.results = [];
    this.addedFileIds = [];
  }

  payloadHandler(action: FluxAction): boolean {
    switch (action.actionType) {
      case EventConstants.REGISTRY_VALIDATOR_CHECK: {
        this.myCollectionErrorsByFileId = null;
        this.orgCollectionErrorsByFileId = null;
        this.isLoading = true;
        this.emitChange();
        break;
      }

      case EventConstants.REGISTRY_VALIDATOR_CHECK_COMPLETED: {
        const {myCollectionErrorsByFileId, orgCollectionErrorsByFileId} = action;
        this.myCollectionErrorsByFileId = myCollectionErrorsByFileId;
        this.orgCollectionErrorsByFileId = orgCollectionErrorsByFileId;
        this.isLoading = false;
        this.results = action.files;
        this.addedFileIds = action.addedFileIds;
        this.emitChange();
        break;
      }

      case EventConstants.REGISTRY_VALIDATOR_MOVE: {
        this.isLoading = true;
        this.emitChange();
        break;
      }

      case EventConstants.REGISTRY_VALIDATOR_CANCEL: {
        this.resetModalState();
        this.emitChange();
        break;
      }

      case EventConstants.REGISTRY_VALIDATOR_MOVE_COMPLETED: {
        this.resetModalState();
        // eslint-disable-next-line no-restricted-syntax
        this.tagErrorsByFileId = _.omit(
          this.tagErrorsByFileId,
          (tagErrors: Array<SchemaValidationErrorRecord>, fileId: string) => action.fileIds.includes(fileId)
        );
        this.emitChange();
        break;
      }

      case EventConstants.REGISTRY_VALIDATOR_VALIDATE_FILE_TAGS_COMPLETED: {
        this.tagErrorsByFileId[action.fileId] = action.errors;
        this.emitChange();
        break;
      }

      case EventConstants.REGISTRY_VALIDATOR_BULK_VALIDATE_FILE_TAGS_COMPLETED: {
        this.tagErrorsByFileId = {...this.tagErrorsByFileId, ...action.errorsByFileId};
        this.emitChange();
        break;
      }

      default:
        break;
    }
    return true;
  }
}

export default new RegistryValidatorStore(AppDispatcher);
